import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { FaWhatsapp, FaTelegramPlane, FaMoneyBillWave } from "react-icons/fa";
import {
  ChevronDown,
  LogOut,
  Settings,
  Home,
  MessageSquare,
  Mail,
  Phone,
  Target,
  Workflow,
  Bot,
  Users,
  BarChart3,
  ChevronRight,
  Plus,
  Database,
  Command,
  Cuboid,
  Zap,
  DatabaseBackupIcon,
  Radio,
  Moon,
  Sun,
  Layers,
  Sparkles,
  RefreshCw,
  ArrowLeft,
} from "lucide-react";
import MessagingRateModal from "../../../pages/settings/settings-modal";
import useRequest from "../../hook/use-req";
import { NavigationApiResponse, ResourceNode, UserResponse } from "../../types";
import { ClockLoader } from "react-spinners";
import { toast } from "sonner";

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
}

const Sidebar1: React.FC<SidebarProps> = ({ open, onToggle }) => {
  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [resources, setResources] = useState<NavigationApiResponse | null>(null);
  const [user, setUser] = useState<UserResponse | null>(null);
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    if (!token) {
      const reloadCount = Number(sessionStorage.getItem("reloadCount") || "0");
      if (reloadCount < 1) {
        sessionStorage.setItem("reloadCount", String(reloadCount + 1));
        window.location.reload();
      } else {
        sessionStorage.removeItem("reloadCount");
      }
    }
  }, [token, navigate]);

  const { makeRequest: getResourceData, loading } = useRequest(`/auth/resources`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  const { makeRequest: userLogOut } = useRequest(`/auth/logout`, "POST", {
    Authorization: `Bearer ${token}`,
  });

  const { makeRequest: getUserData } = useRequest(`/auth/get_userdata`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  const handleSignOut = async () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("resetOtpCode");
    localStorage.removeItem("userDetails");
    navigate("/");

    try {
      const [response] = await userLogOut();
      if (response.status === 200) {
        toast.success(response.message);
        ["campaignId", "shortenedUrl", "uploadedAudience", "validRecords"].forEach((key) =>
          localStorage.removeItem(key)
        );
        setTimeout(() => {
          if (response.redirect_url) {
            window.location.href = response.redirect_url;
          } else {
            window.location.reload();
          }
        }, 1000);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      ["campaignId", "shortenedUrl", "uploadedAudience", "validRecords"].forEach((key) =>
        localStorage.removeItem(key)
      );
      navigate("/");
      window.location.reload();
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) =>
      prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section]
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      const [response] = await getUserData();
      setUser(response);
    };
    fetchData();
  }, []);

  const getIconForResource = (resourceName: string) => {
    const iconMap: Record<string, any> = {
      Home: Home,
      Broadcasts: Radio,
      Templates: Layers,
      Analytics: BarChart3,
      "Bot Studio": Bot,
      "Bot_Studio": Bot,
      BotStudio: Bot,
      Settings: Settings,
      "Convo AI": Sparkles,
      ConvoAI: Sparkles,
      Convo_AI: Sparkles,
      CDP: Cuboid,
      Communication: Command,
      Channel: MessageSquare,
      SMS: MessageSquare,
      Whatsapp: FaWhatsapp,
      Email: Mail,
      RCS: MessageSquare,
      Voice: Phone,
      Webchat: MessageSquare,
      Telegram: FaTelegramPlane,
      Campaign: Target,
      Reseller: Target,
      Campaigns: Target,
      AllCampaigns: Target,
      CreateCampaigns: Plus,
      Automations: Workflow,
      Workflows: Workflow,
      Triggers: Zap,
      AI_Agents: Bot,
      AIAgents: Bot,
      Sequences: Workflow,
      Contacts: Users,
      Data: Database,
      Connectors: Database,
      Sources: Database,
      Destinations: Database,
      UseCases: Workflow,
      Audience: Users,
      Overview: BarChart3,
      ChannelPerformance: BarChart3,
      CampaignReports: BarChart3,
      Engagement: BarChart3,
      Wallet: FaMoneyBillWave,
      Team: Users,
      APIKeys: DatabaseBackupIcon,
      AutomationSettings: Settings,
    };
    return iconMap[resourceName] || MessageSquare;
  };

  const getPathForResource = (resourceName: string, parentName?: string) => {
    const pathMap: Record<string, string> = {
      Home: "/cpas-dashboard",
      Broadcasts: "/campaign",
      Templates: "#",
      Analytics: "#",
      "Bot Studio": "#",
      BotStudio: "#",
      Bot_Studio: "#",
      Settings: "/settings",
      "Convo AI": "/convoAi",
      ConvoAI: "/convoAi",
      
      CDP: "/cdp",
      SMS: "/dashboard",
      Whatsapp: "/whatsapp-setup",
      Email: "/email",
      RCS: "/rcs-setup",
      Voice: "/voice-setup",
      Webchat: "/webchat-setup",
      Telegram: "/telegram-setup",
      Campaign: "/campaign",
      Campaigns: "/campaign",
      Reseller: "/customer",
      AllCampaigns: "/campaign",
      CreateCampaigns: "/campaigns/create",
      Workflows: "/journeys",
      Triggers: "/triggers",
      AI_Agents: "/#automation/ai-agents",
      Sequences: "/sequences",
      Contacts: "/contact",
      Sources: "/connectors/sources",
      Destinations: "/connectors/destinations",
      UseCases: "/use-cases",
      Audience: "/audience",
      Overview: "/analytics",
      ChannelPerformance: "/analytics/channels",
      CampaignReports: "/analytics/campaigns",
      Engagement: "/analytics/engagement",
      Wallet: "/wallet",
      Team: "/settings/team",
      APIKeys: "/settings/api",
      AutomationSettings: "/settings/automation",
    };
    return pathMap[resourceName] || "#";
  };

  const transformResourceToNavigation = (
    resources: Record<string, ResourceNode>,
    parentKey?: string
  ): any[] => {
    return Object.entries(resources)
      .filter(([_, permission]) => !permission.allowed || permission.allowed === "view")
      .map(([key, permission]) => {
        const hasChildren = Object.keys(permission.subnodes).length > 0;
        const Icon = getIconForResource(key);
        const item: any = {
          id: key.toLowerCase().replace(/\s+/g, "_"),
          label: key.replace(/_/g, " "),
          icon: Icon,
          path: getPathForResource(key, parentKey),
          expandable: hasChildren,
        };

        if (key === "SetMessagingRates") {
          item.onClick = () => setIsModalOpen(true);
          delete item.path;
        }

        if (hasChildren) {
          item.children = transformResourceToNavigation(permission.subnodes, key);
        }

        return item;
      });
  };

  useEffect(() => {
    const fetchData = async () => {
      const [response] = await getResourceData();
      if (response) {
        setResources(response);
        if (!sessionStorage.getItem("didReload")) {
          sessionStorage.setItem("didReload", "true");
          window.location.reload();
        }
      }
    };
    fetchData();
  }, []);

  const navigationItems = resources?.payload?.navigation?.subnodes
    ? transformResourceToNavigation(resources.payload.navigation.subnodes).filter(
        (item) => item.id !== "quickactions"
      )
    : [];

  const renderNavItem = (item: any, level = 0) => {
    const Icon = item.icon;
    const isExpanded = expandedSections.includes(item.id);
    const isActive =
      location.pathname === item.path ||
      (item.children &&
        item.children.some((child: any) => location.pathname === child.path));

    const hasOnClick = item.onClick && typeof item.onClick === "function";

    const content = (
      <div
        className={classNames(
          "flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-150 group",
          {
            "bg-[#EEF2FF] text-[#4F46E5]": isActive,
            "text-gray-500 hover:bg-gray-100 hover:text-gray-800": !isActive,
            "ml-3": level > 0,
          }
        )}
      >
        {Icon && (
          <Icon
            size={18}
            className={classNames("flex-shrink-0 transition-colors", {
              "text-[#4F46E5]": isActive,
              "text-gray-400 group-hover:text-gray-600": !isActive,
            })}
          />
        )}
        {open && (
          <span
            className={classNames("text-sm font-medium flex-1 truncate", {
              "text-[#4F46E5]": isActive,
              "text-gray-600 group-hover:text-gray-800": !isActive,
            })}
          >
            {item.label}
          </span>
        )}
        {open && item.expandable && (
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleSection(item.id);
            }}
            className="ml-auto"
          >
            {isExpanded ? (
              <ChevronDown size={14} className="text-gray-400" />
            ) : (
              <ChevronRight size={14} className="text-gray-400" />
            )}
          </div>
        )}
      </div>
    );

    return (
      <div key={item.id}>
        {hasOnClick ? (
          <div onClick={item.onClick}>{content}</div>
        ) : (
          <Link to={item.path || "#"}>{content}</Link>
        )}

        {item.expandable && isExpanded && item.children && open && (
          <div className="mt-1 space-y-0.5">
            {item.children.map((child: any) => renderNavItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };
  return (
    <div
      className={classNames(
        "flex flex-col h-full border-r transition-all duration-300",
        darkMode ? "bg-[#111827] border-gray-800" : "bg-white border-gray-100"
      )}
      // style={{ width: open ? "220px" : "64px" }}
    >
   <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          {/* Chat bubble icon */}
          <div className="flex items-center justify-center w-7 h-7">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              className="w-5 h-5 text-gray-700"
            >
              <path
                d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            </svg>
          </div>
           {open && (
          <span className="text-sm gap-1 font-semibold flex text-gray-800 tracking-tight">
            ConvoHub{" "}
            <span className="text-blue-500 font-semibold">Marketing</span>
          </span>
           )}

        </div>

           {open && (
        <div className="flex items-center gap-1.5">
          {/* Refresh */}
          <button
            className="flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
            onClick={() => window.location.reload()}
            title="Refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>

          {/* Back arrow */}
          <button
            className="flex items-center justify-center w-7 h-7 rounded-md border border-gray-200 text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
            onClick={() => navigate('/crm/dashboard')}
            title="Go to CRM"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
          </button>
        </div>
           )}
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-3 px-2">
        {loading ? (
          <div className="flex justify-center mt-10">
            <ClockLoader color="#4F46E5" loading={loading} size={32} />
          </div>
        ) : (
          <nav className="space-y-0.5">
            {navigationItems.map((item) => renderNavItem(item))}
          </nav>
        )}
      </div>

      {/* Bottom Section */}
      <div
        className={classNames("border-t px-2 py-3 space-y-1", {
          "border-gray-800": darkMode,
          "border-gray-100": !darkMode,
        })}
      >
        {/* Dark Mode Toggle */}
        <div
          className={classNames(
            "flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all",
            darkMode
              ? "text-gray-400 hover:bg-gray-800"
              : "text-gray-500 hover:bg-gray-100"
          )}
          onClick={() => setDarkMode(!darkMode)}
        >
          {darkMode ? (
            <Sun size={18} className="flex-shrink-0 text-gray-400" />
          ) : (
            <Moon size={18} className="flex-shrink-0 text-gray-400" />
          )}
          {open && (
            <>
              <span
                className={classNames("text-sm font-medium flex-1", {
                  "text-gray-300": darkMode,
                  "text-gray-600": !darkMode,
                })}
              >
                Dark Mode
              </span>
              {/* Toggle switch */}
              <div
                className={classNames(
                  "w-9 h-5 rounded-full transition-colors relative flex-shrink-0",
                  darkMode ? "bg-[#4F46E5]" : "bg-gray-200"
                )}
              >
                <div
                  className={classNames(
                    "absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all",
                    darkMode ? "left-4.5 translate-x-0" : "left-0.5"
                  )}
                  style={{ left: darkMode ? "18px" : "2px" }}
                />
              </div>
            </>
          )}
        </div>

        {/* Log Out */}
        <div
          className={classNames(
            "flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all",
            darkMode
              ? "text-gray-400 hover:bg-gray-800"
              : "text-gray-500 hover:bg-gray-100"
          )}
          onClick={handleSignOut}
        >
          <LogOut size={18} className="flex-shrink-0 text-gray-400" />
          {open && (
            <span
              className={classNames("text-sm font-medium", {
                "text-gray-300": darkMode,
                "text-gray-600": !darkMode,
              })}
            >
              Log Out
            </span>
          )}
        </div>
      </div>

      <MessagingRateModal isOpen={isModalOpen} setIsOpen={setIsModalOpen} />
    </div>
  );
};

export default Sidebar1;